import React from 'react';
import {
    IconButton,
    Tooltip,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Paper,
    Divider,
} from '@mui/material';
import {
    Palette as PaletteIcon,
    Brightness7 as LightIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeToggle = ({ variant = 'icon', showLabel = false }) => {
    const { currentTheme, toggleTheme, themeNames, THEME_OPTIONS } = useTheme();

    const isPulseTheme = currentTheme === THEME_OPTIONS.PULSE;

    // Icon-only variant (for navigation)
    if (variant === 'icon') {
        return (
            <Tooltip title={isPulseTheme ? 'Switch to Classic theme' : 'Switch to Pulse theme'}>
                <IconButton
                    onClick={toggleTheme}
                    color="inherit"
                    aria-label={isPulseTheme ? 'Switch to Classic theme' : 'Switch to Pulse theme'}
                    sx={{
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(245, 166, 35, 0.1)',
                        },
                    }}
                >
                    <PaletteIcon />
                </IconButton>
            </Tooltip>
        );
    }

    // Switch variant (for settings)
    if (variant === 'switch') {
        return (
            <FormControlLabel
                control={
                    <Switch
                        checked={isPulseTheme}
                        onChange={toggleTheme}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#f5a623',
                                '&:hover': {
                                    backgroundColor: 'rgba(245, 166, 35, 0.08)',
                                },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#f5a623',
                            },
                        }}
                    />
                }
                label={showLabel ? `Pulse Theme` : ''}
            />
        );
    }

    // Card variant (for theme selection)
    if (variant === 'card') {
        return (
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: isPulseTheme ? '2px solid #f5a623' : '2px solid transparent',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        elevation: 4,
                        transform: 'translateY(-2px)',
                    },
                }}
                onClick={toggleTheme}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {themeNames[currentTheme]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isPulseTheme
                                ? 'Professional Services Automation with Pulse branding'
                                : 'Standard Material-UI theme'
                            }
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {isPulseTheme ? (
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #2563ff 0%, #f5a623 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                P
                            </Box>
                        ) : (
                            <PaletteIcon color="action" />
                        )}
                    </Box>
                </Box>
            </Paper>
        );
    }

    // Detailed variant (for theme showcase)
    if (variant === 'detailed') {
        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Theme Selection
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" gap={2} flexWrap="wrap">
                    {/* Original Theme Card */}
                    <Paper
                        elevation={currentTheme === THEME_OPTIONS.ORIGINAL ? 4 : 1}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: currentTheme === THEME_OPTIONS.ORIGINAL
                                ? '2px solid #2196f3'
                                : '2px solid transparent',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            minWidth: 200,
                            '&:hover': {
                                elevation: 4,
                                transform: 'translateY(-2px)',
                            },
                        }}
                        onClick={() => toggleTheme()}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    backgroundColor: '#2196f3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                }}
                            >
                                <LightIcon />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {themeNames[THEME_OPTIONS.ORIGINAL]}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Standard Material-UI
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Pulse Theme Card */}
                    <Paper
                        elevation={currentTheme === THEME_OPTIONS.PULSE ? 4 : 1}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: currentTheme === THEME_OPTIONS.PULSE
                                ? '2px solid #f5a623'
                                : '2px solid transparent',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            minWidth: 200,
                            '&:hover': {
                                elevation: 4,
                                transform: 'translateY(-2px)',
                            },
                        }}
                        onClick={() => toggleTheme()}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #2563ff 0%, #f5a623 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                }}
                            >
                                P
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {themeNames[THEME_OPTIONS.PULSE]}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Professional Services Automation
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                        Current theme: <strong>{themeNames[currentTheme]}</strong>
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Default: simple button
    return (
        <Tooltip title={`Switch to ${isPulseTheme ? 'Classic' : 'Pulse'} theme`}>
            <IconButton
                onClick={toggleTheme}
                color="inherit"
                sx={{
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'rgba(245, 166, 35, 0.1)',
                    },
                }}
            >
                <PaletteIcon />
            </IconButton>
        </Tooltip>
    );
};

export default ThemeToggle;
